'use client';

import React, { useState } from 'react';
import { ApiRequestConfig, DataFetcher } from '../utils/DataFetcher';
import { ComponentNode } from '../types/template';
import { getNestedValue } from '../utils/dataBinding';
import ComponentRenderer from './ComponentRenderer';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';

interface RenderContext {
    companyId?: string;
    templateId?: string;
    domain?: string;
    pageSlug?: string;
    params?: Record<string, any>;
    searchParams?: Record<string, any>;
    store_slug?: string;
    slug_type?: string;
    category_slug?: string;
    promotion_slug?: string;
    out_link?: string;
}

interface LoadMorePagination {
    current_page?: number;
    last_page?: number;
}

function arrayFromUnknown(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
}

/**
 * Resolves a repeated node's array the same way DynamicSectionComponent's own
 * (unexported) resolveCollection does for its `dataSourceKey`-keyed branch —
 * the only branch Offers Banner's response shapes actually use (a flat
 * `data.banners[]` or `data.offers[]`, confirmed via Postman; see
 * ComponentNode.loadMore's doc comment in theme-builder's
 * packages/template-renderer/src/types.ts). Not a full reimplementation of
 * every resolveCollection branch — narrowly scoped to what this node needs.
 */
function resolveDataSourceArray(responseData: any, dataSourceKey: string | undefined): any[] {
    if (!dataSourceKey) return [];
    const keyed =
        getNestedValue(responseData, dataSourceKey) ??
        getNestedValue(responseData?.data, dataSourceKey) ??
        getNestedValue(responseData?.data?.[0], dataSourceKey);
    return arrayFromUnknown(keyed);
}

/**
 * Applies ComponentNode.sizeFilter (see its doc comment in theme-builder's
 * packages/template-renderer/src/types.ts) using the REAL, already-tested
 * `filterOfferBanners` from src/constants/hooks.ts rather than
 * reimplementing it here — per explicit instruction, this storefront path
 * must not duplicate that filter logic.
 *
 * `filterOfferBanners`/`getBannerDimensions` are hardcoded to read
 * `item.offer.banner_size` and a fixed [0.75, 1.35] ratio range (see
 * hooks.ts) — they don't take a configurable field path or ratio bounds.
 * theme-builder's structures.ts only ever sets `sizeField: "offer.banner_size"`
 * with that same ratio range (the only shape that currently exists), so this
 * narrow bridge calls the real function when that's what's configured, and
 * falls through to "no filtering" (rather than a silent wrong-field filter)
 * for any other sizeField this JSON contract doesn't produce yet.
 */
function applySizeFilter(items: any[], sizeFilter?: ComponentNode['sizeFilter']): any[] {
    if (!sizeFilter) return items;
    if (sizeFilter.sizeField !== 'offer.banner_size') return items;
    return filterOfferBanners(
        items,
        sizeFilter.minWidth,
        sizeFilter.maxWidth,
        sizeFilter.minHeight,
        sizeFilter.maxHeight,
    );
}

function computeHasMore(response: unknown, paginationKey: string): boolean {
    const pagination = getNestedValue(response, paginationKey) as LoadMorePagination | undefined;
    if (!pagination) return false;
    const current = Number(pagination.current_page);
    const last = Number(pagination.last_page);
    if (!Number.isFinite(current) || !Number.isFinite(last)) return false;
    return current < last;
}

interface LoadMoreCardListProps {
    /** The repeated CARD node template (carries `loadMore`, `dataSourceKey`). */
    cardTemplate: ComponentNode;
    /** Page 1's items, already resolved server-side by DynamicSectionComponent. */
    initialItems: any[];
    /** Page 1's raw API response, read for its `pagination` object only. */
    initialResponse: any;
    /** The section's apiConfig, reused (same endpoint/body) for each next page. */
    apiConfig: ApiRequestConfig;
    gridStyle: React.CSSProperties;
    context?: RenderContext;
}

const LoadMoreCardList: React.FC<LoadMoreCardListProps> = ({
    cardTemplate,
    initialItems,
    initialResponse,
    apiConfig,
    gridStyle,
    context,
}) => {
    const loadMore = cardTemplate.loadMore as {
        param: string;
        pageSizeParam: string;
        pageSize: number;
        paginationKey: string;
    };
    const isBannerOfferList =
        cardTemplate.dataSourceKey === 'data.offers' &&
        apiConfig.apiEndpoint.includes('get_custom_builder_banners');
    const sizeFilter = cardTemplate.sizeFilter ?? (isBannerOfferList ? {
        sizeField: 'offer.banner_size',
        minWidth: 50,
        maxWidth: 2000,
        minHeight: 65,
        maxHeight: 2000,
        minRatio: 0.75,
        maxRatio: 1.35,
    } : undefined);
    const [items, setItems] = useState<any[]>(() => applySizeFilter(initialItems, sizeFilter));
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(() => computeHasMore(initialResponse, loadMore.paginationKey));

    const handleLoadMore = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const nextPage = page + 1;
            const nextRequest: ApiRequestConfig = {
                ...apiConfig,
                body: {
                    ...(apiConfig.body ?? {}),
                    [loadMore.param]: nextPage,
                    [loadMore.pageSizeParam]: loadMore.pageSize,
                },
            };

            const response = await DataFetcher.fetchRawData(nextRequest, {
                companyId: context?.companyId,
                domain: context?.domain,
            });

            const newItems = resolveDataSourceArray(response, cardTemplate.dataSourceKey);
            const filteredNewItems = applySizeFilter(newItems, sizeFilter);
            setItems((prev) => [...prev, ...filteredNewItems]);
            setPage(nextPage);
            setHasMore(computeHasMore(response, loadMore.paginationKey));
        } catch {
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div style={gridStyle} className="cb-responsive-grid">
                {items.map((item, index) => (
                    <ComponentRenderer
                        key={`${cardTemplate.id}-${index}`}
                        component={cardTemplate}
                        data={item}
                        context={context}
                    />
                ))}
            </div>
            {hasMore && (
                <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    className="mx-auto mt-4 block rounded-full border px-6 py-2 text-[12px] font-bold disabled:opacity-60"
                    style={{
                        borderColor: 'var(--theme-color-primary, #f23017)',
                        color: 'var(--theme-color-primary, #f23017)',
                    }}
                >
                    {isLoading ? 'Loading…' : 'Show More'}
                </button>
            )}
        </>
    );
};

export default LoadMoreCardList;
